'use client';

import {
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
} from '@mui/material';
import { useState } from 'react';

import type { FieldValidation } from './validation/fieldValidation';
import { InputErrorIcon } from './validation/inputErrorIcon';

export const PasswordTextField = ({
  title,
  dataTestId,
  validation,
  placeholder,
  value,
  onChange,
  sx,
}: {
  title: string;
  dataTestId?: string;
  validation?: FieldValidation;
  placeholder: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps;
}) => {
  const [touched, setTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const hasError =
    touched && (validation ? !validation.validateInput(value) : false);

  return (
    <FormControl sx={{ width: '100%', ...sx }}>
      {title && (
        <FormLabel>
          <Typography variant='subtitle1'>{title}</Typography>
        </FormLabel>
      )}
      <TextField
        type={showPassword ? 'text' : 'password'}
        onBlur={() => setTouched(true)}
        error={hasError}
        sx={{
          '& .MuiInputBase-root': {
            paddingRight: '4px',
          },
        }}
        slotProps={{
          htmlInput: {
            ...(dataTestId ? { 'data-testid': dataTestId } : {}),
            style: { height: '24px' },
            autoComplete: 'new-password',
            'aria-label': title || 'Password',
          },
          input: {
            endAdornment: (
              <>
                {hasError && (
                  <InputErrorIcon
                    errorMessage={validation?.errorMessage(value)}
                  />
                )}
                <InputAdornment position='end'>
                  <Tooltip
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <IconButton
                      size='small'
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge='end'
                      data-testid='passwordVisibilityToggle'
                    >
                      <i
                        className={
                          showPassword
                            ? 'material-symbols-visibility-off-outline-rounded'
                            : 'material-symbols-visibility-outline-rounded'
                        }
                      />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              </>
            ),
          },
        }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
    </FormControl>
  );
};
